/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { StyledContainer } from '../../Content.styles';
import {
  StyledSubheader,
  StyledHeadline,
  StyledParagraph,
  useStyles,
} from './MalpracticeReportsSummary/MalpracticeReportsSummary.styles';
import { LinkButton } from '@cambridgeassessment/cambridge-ui';
import { RootState } from '../../../../store';
import { ReportDetails, SearchQuery } from '../../../../types/Plagiarism';
import * as thunks from '../../../../store/malpracticeReports/thunks';
import Loader from '../../../../components/Loader/Loader';
import * as actions from '../../../../store/malpracticeReports/actions';
import { SectionBusinessId, SortOrder, Roles } from '@cambridgeassessment/lib-ee-commons';
import ReportTable, { SortingParams, useSorting } from './ReportTable/ReportTable';
import { formatDate } from '../../../../util/dateFormatter';
import { Authorized } from 'lib-browser';

interface Props {
  match: {
    params: {
      seriesId: number;
      assessmentCode: string;
      componentNumber: string;
      sectionId: number;
    };
  };
  queryParams: {
    searchQuery: Omit<SearchQuery, 'sectionId'>;
    low: number;
    high: number;
  };
  fetchComponentDetails: (x: Omit<SectionBusinessId, 'centreId' | 'learnerNumber' | 'learnerUniqueId'>) => void;
}

const ReportSummaryDetails: React.FC<Props> = ({
  match: {
    params: { seriesId, assessmentCode, componentNumber, sectionId },
  },
  fetchComponentDetails,
  queryParams,
}) => {
  const dispatch = useDispatch();
  const {
    malpracticeReport: { details },
    userData: { selectedRole },
  } = useSelector((state: RootState) => state);
  const reportParams = details.reportDetailsParams;
  const reportDetails = details.similarityRanges;

  useEffect(() => {
    fetchComponentDetails({ seriesId, assessmentCode, componentNumber, sectionId });
  }, [fetchComponentDetails, seriesId, assessmentCode, componentNumber, sectionId]);

  const loadMoreItems = useCallback((sortingParams?: SortingParams<ReportDetails>) => {
    const ranges = {
      low: queryParams.low,
      high: queryParams.high,
    };

    dispatch(
      thunks.getReportDetails(
        { seriesId, assessmentCode, componentNumber, sectionId },
        queryParams.searchQuery,
        ranges,
        sortingParams,
      ),
    );
  }, []);

  useEffect(() => {
    loadMoreItems();
  }, []);

  const { sort: onSort } = useSorting<ReportDetails>(
    (sortingParams) => {
      dispatch(actions.resetState());
      loadMoreItems(sortingParams);
    },
    {
      fieldName: 'centreId',
      order: SortOrder.ASC,
    },
  );

  const openUrl = (submissionId: string): void => {
    dispatch(thunks.getTurnitinUrl(submissionId));
  };
  const classes = useStyles();

  if (!reportDetails) return <Loader />;

  return (
    <Authorized
      selectedRole={selectedRole}
      roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER]}
    >
      {reportParams && (
        <StyledContainer data-qa-id='report-details-series'>
          <StyledSubheader>{reportParams.seriesName}</StyledSubheader>
          <StyledHeadline>
            {reportParams.assessmentCode} &ndash; {reportParams.componentNumber} - {reportParams.componentName} -{' '}
            {reportParams.sectionName}
          </StyledHeadline>
          <StyledParagraph>Explore Similarity Index Reports</StyledParagraph>
        </StyledContainer>
      )}
      <ReportTable
        data={reportDetails}
        columns={[
          {
            label: 'Centre number',
            name: 'centreId',
            centre: true,
            sortable: true,
          },
          {
            label: 'Candidate name',
            name: 'learner',
            centre: false,
            sortable: true,
          },
          {
            label: 'Candidate number',
            name: 'learnerNumber',
            centre: true,
            sortable: true,
          },
          {
            label: 'Similarity',
            name: 'similarityPercentage',
            centre: true,
            sortable: true,
          },
          {
            label: 'Word count',
            name: 'wordCount',
            centre: true,
            sortable: true,
          },
          {
            label: 'Submission date',
            name: 'createdTime',
            centre: true,
            sortable: true,
            renderItem: (row) => formatDate(row.createdTime),
          },
          {
            label: 'Report links',
            name: 'submissionId',
            centre: true,
            sortable: false,
            renderItem: function TurnItInErrorsLink(row: ReportDetails) {
              return (
                <LinkButton
                  arrowType='down'
                  endIcon={false}
                  dataQaId='turnItIn-link'
                  onClick={(): void => openUrl(row.submissionId)}
                  passedClassName={classes.root}
                >
                  Turnitin link
                </LinkButton>
              );
            },
          },
        ]}
        loadMoreItems={loadMoreItems}
        onSort={onSort}
      />
    </Authorized>
  );
};

export default ReportSummaryDetails;
